import {useState, useEffect, useMemo} from 'react';
import {Form, message} from 'antd';
import {useQuery, useMutation} from '@apollo/client';
import {GET_VEHICLES_BY_WAREHOUSE_ID,VehicleType,GetVehiclesByWarehouseIdResponse} from '../../../../hooks/useVehicleOptions';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";

import {
    GET_PICKUP_POINT_OPTIONS,
    GetPickupPointOptionsResponse,
} from '../../pickup-task/graphql/queries/getPickupPointOptions';
import {GET_PICKUP_DRIVERS_BY_WAREHOUSE_ID} from '../../pickup-task/graphql/queries/getPickupDriversByWarehouseId';
import {
    GET_PICKUP_APPOINTMENTS_BY_ID_LIST,
    GetPickupAppointmentsByIdListResponse
} from '../graphql/queries/getPickupAppointmentsByIdList';
import {PickupAppointmentType} from '../graphql/queries/getPickupAppointmentListing';
import {packageUnitFilterOptions} from '../types/PickupAppointmentType';
import {CREATE_PICKUP_TASK_WITH_APPOINTMENTS} from '../graphql/mutations/createPickupTaskWithAppointments';
import {GET_HUB_LIST, GetHubListResponse, HubType} from '../graphql/queries/getHubList';
import {useVehicleTypes} from '../../../../hooks/useVehicleTypeOptions';
import i18n from '../../../../i18n';
import { PickupSourceEnum } from '../../../../enums/PickupSourceEnum';
import dayjs from 'dayjs';

interface PickupTaskCreateDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    selectedAppointmentIds?: number[];
    pickupDate?: string; // 格式：YYYY-MM-DD
    useSystemRouting?: boolean; // 是否使用系统排序
    taskSource?: PickupSourceEnum; // 创建任务来源
}

interface PickupPointOption {
    label: string;
    value: string;
    address: string;
    zipcode: string;
    pickupDeadlineTime?: string; // 添加pickupDeadlineTime字段
}

interface PickupDriver {
    driverName: string;
    accountId: string;
    status: number;
    vehicleId?: number | null;
}

export const usePickupTaskCreateDrawer = ({
                                              visible,
                                              onClose,
                                              onSuccess,
                                              selectedAppointmentIds = [],
                                              pickupDate,
                                              useSystemRouting = true,
                                              taskSource
                                          }: PickupTaskCreateDrawerProps) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id;
    const [appointmentList, setAppointmentList] = useState<PickupAppointmentType[]>([]);
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [summaryUpdateKey, setSummaryUpdateKey] = useState(0);
    // 添加选中的车型ID状态
    const [selectedVehicleModelId, setSelectedVehicleModelId] = useState<number | null>(null);
    const [selectedDestinationHub, setSelectedDestinationHub] = useState<HubType | null>(null);
    // 添加选中的司机ID状态
    const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);

    // Reset form and state when drawer visibility changes
    useEffect(() => {
        if (visible) {
            // Reset form to initial values when drawer opens
            form.resetFields();
            // 使用传入的 pickupDate 或默认为今天
            const taskDate = pickupDate ? dayjs(pickupDate) : dayjs();
            form.setFieldsValue({
                taskDate: taskDate, // 设置任务日期为传入的 pickupDate
                status: 'DRAFT' // 设置默认状态
            });
            setAppointmentList([]); // 清空预约列表
            setAddModalVisible(false); // 关闭添加模态框
            setSelectedVehicleModelId(null); // 重置选中的车型
            setSelectedDestinationHub(null);
            setSelectedDriverId(null); // 重置选中的司机
        } else {
            // Clear all state when drawer closes
            form.resetFields();
            setAppointmentList([]);
            setAddModalVisible(false);
            setLoading(false);
            setSelectedVehicleModelId(null);
            setSelectedDestinationHub(null);
            setSelectedDriverId(null);
        }
    }, [visible, form]);

    // Reset appointment list when selectedAppointmentIds changes and drawer is visible
    useEffect(() => {
        if (visible && selectedAppointmentIds.length === 0) {
            setAppointmentList([]);
        }
    }, [selectedAppointmentIds, visible]);

    // Get drivers for the current warehouse
    const {data: driverData, loading: driversLoading} = useQuery(GET_PICKUP_DRIVERS_BY_WAREHOUSE_ID, {
        variables: {warehouseId: warehouseId, accountStatus: 1}, // 默认只查询启用的司机
        skip: !warehouseId || !visible, // 只在抽屉可见时查询
    });

    // Get pickup points for the current warehouse
    const {
        data: pickupPointsData,
        loading: pickupPointsLoading
    } = useQuery<GetPickupPointOptionsResponse>(GET_PICKUP_POINT_OPTIONS, {
        variables: {
            input: {
                warehouseId
            }
        },
        skip: !warehouseId || !visible, // 只在抽屉可见时查询
    });

    // Get vehicle types
    const { vehicleTypes, loading: vehicleModelsLoading } = useVehicleTypes();

    // Get hub list for pickup deadline time
    const {data: hubListData, loading: hubListLoading} = useQuery<GetHubListResponse>(GET_HUB_LIST);

    // Get selected appointments data
    const {data: appointmentsData, loading: appointmentsLoading} = useQuery<GetPickupAppointmentsByIdListResponse>(
        GET_PICKUP_APPOINTMENTS_BY_ID_LIST,
        {
            variables: {appointmentIds: selectedAppointmentIds},
            skip: !visible || selectedAppointmentIds.length === 0,
        }
    );

    // 获取包含vehicleModelId的完整车辆数据用于过滤
    const {data: vehiclesWithModelData, loading: vehiclesWithModelLoading} = useQuery<GetVehiclesByWarehouseIdResponse>(
        GET_VEHICLES_BY_WAREHOUSE_ID,
        {
            variables: {warehouseId: warehouseId},
            skip: !warehouseId || !visible, // 只在抽屉可见时查询
        }
    );

    // Create task mutation
    const [createPickupTask] = useMutation(CREATE_PICKUP_TASK_WITH_APPOINTMENTS);

    // Update appointment list when data is loaded and drawer is visible
    // 确保按照 selectedAppointmentIds 的顺序排列预约单
    useEffect(() => {
        if (visible && appointmentsData?.pickupAppointments) {
            console.log('📋 usePickupTaskCreateDrawer - 开始排序预约单列表');
            console.log('📋 selectedAppointmentIds:', selectedAppointmentIds);
            console.log('📋 appointmentsData.pickupAppointments 数量:', appointmentsData.pickupAppointments.length);
            
            // 创建一个 Map 用于快速查找预约单
            const appointmentMap = new Map<number, PickupAppointmentType>();
            appointmentsData.pickupAppointments.forEach((apt: PickupAppointmentType) => {
                appointmentMap.set(apt.id, apt);
            });
            
            // 按照 selectedAppointmentIds 的顺序排列预约单
            const orderedAppointments: PickupAppointmentType[] = [];
            selectedAppointmentIds.forEach((id: number) => {
                const appointment = appointmentMap.get(id);
                if (appointment) {
                    orderedAppointments.push(appointment);
                } else {
                    console.warn(`⚠️ 预约单 ID ${id} 在查询结果中未找到`);
                }
            });
            
            // 如果 selectedAppointmentIds 中有ID在查询结果中找不到，添加剩余的预约单（虽然理论上不应该发生）
            appointmentsData.pickupAppointments.forEach((apt: PickupAppointmentType) => {
                if (!selectedAppointmentIds.includes(apt.id)) {
                    orderedAppointments.push(apt);
                }
            });
            
            console.log('📋 排序后的预约单列表顺序:', orderedAppointments.map(apt => apt.id));
            setAppointmentList(orderedAppointments);
        }
    }, [appointmentsData, visible, selectedAppointmentIds]);

    // Handle form submission
    const handleSave = async (publishTask: boolean = false) => {
        try {
            const values = await form.validateFields();

            // Process date and time fields
            const startPoint = values.startPoint || {};
            const startPointType = startPoint.type;
            const startPointValue = startPoint.value;
            // Split startPointValue and endPointValue from format "type_value" to separate fields
            const [endPointType, endPointValue] = values.endPointValue ? values.endPointValue.split('_') : [null, null];

            const processedValues: any = {
                taskDate: values.taskDate?.format('YYYY-MM-DD'),
                // 转换为UTC时间戳（秒）
                expDepartureTime: values.expDepartureTime ? values.expDepartureTime.utc().unix() : undefined,
                expCompleteTime: values.expCompleteTime ? values.expCompleteTime.utc().unix() : undefined,
                // 本地时间字符串（格式：YYYY-MM-DD HH:mm:ss）
                expDepartureTimeAt: values.expDepartureTime ? values.expDepartureTime.format('YYYY-MM-DD HH:mm:ss') : undefined,
                expCompleteTimeAt: values.expCompleteTime ? values.expCompleteTime.format('YYYY-MM-DD HH:mm:ss') : undefined,
                // Add required fields with extracted type and value
                startPointValue: startPointValue,
                endPointValue: endPointValue,
                startPointType: startPointType ? parseInt(startPointType, 10) : null,
                endPointType: endPointType ? parseInt(endPointType, 10) : null,
                vehicleTypeId: values.vehicleTypeId ? Number(values.vehicleTypeId) : null,
                warehouseId: warehouseId,
                // 其他必要字段
                accountId: values.accountId,
                vehicleId: values.vehicleId,
                status: publishTask ? 'PUBLISHED' : 'DRAFT',
                appointmentIds: appointmentList.map(item => item.id),
                useSystemRouting: useSystemRouting // 是否使用系统排序
            };

            // 任务来源（从外部组件传入），如果有就传给后端
            if (taskSource) {
                processedValues.taskSource = taskSource;
            }

            setLoading(true);

            // Create new task with appointments
            const result = await createPickupTask({
                variables: {input: processedValues},
            });

            message.success(`Task ${publishTask ? 'published' : 'created'} successfully`);
            setLoading(false);
            if (onSuccess) onSuccess();
            onClose();
        } catch (e: any) {
            setLoading(false);
            message.error(e.message || 'Failed to create task');
        }
    };

    // Prepare driver options
    const drivers = (driverData?.getPickupDriversByWarehouseId || []) as PickupDriver[];
    const driverOptions = drivers.map((driver: PickupDriver) => ({
        label: driver.driverName,
        value: driver.accountId
    })) || [];

    // Handle driver change
    const handleDriverChange = (value: string) => {
        setSelectedDriverId(value);

        // 查找选中司机的车辆信息
        const selectedDriver = drivers.find((driver: PickupDriver) => driver.accountId == value);

        if (selectedDriver?.vehicleId) {
            // 如果司机有关联车辆，自动选择该车辆
            const vehicle = vehiclesWithModelData?.vehicles.find(
                (v: VehicleType) => v.id === selectedDriver.vehicleId
            );

            if (vehicle) {
                // 自动选择车辆
                form.setFieldsValue({
                    vehicleId: vehicle.id
                });

                // 自动选择车辆类型
                if (vehicle.vehicleTypeId) {
                    form.setFieldsValue({
                        vehicleTypeId: vehicle.vehicleTypeId
                    });
                    setSelectedVehicleModelId(vehicle.vehicleTypeId);
                }
            }
        } else {
            // 如果司机没有关联车辆，清空车辆和车型选择
//             form.setFieldsValue({
//                 vehicleModelId: undefined
//             });
            setSelectedVehicleModelId(null);
        }
    };

    // Prepare vehicle type options - show all vehicle types regardless of status
    const vehicleModelOptions = (vehicleTypes || [])
        .map(vt => ({
            label: vt.nameEn,
            value: vt.id  // number，与 selectedVehicleModelId（number）比较，类型一致
        }));

    // 根据选中的车型过滤车辆选项
    const filteredVehicleOptions = useMemo(() => {
        if (!selectedVehicleModelId || !vehiclesWithModelData?.vehicles) {
            return [];
        }

        return vehiclesWithModelData.vehicles
            .filter((vehicle: VehicleType) =>
                vehicle.status === 1 && // 只显示可用状态的车辆
                vehicle.vehicleTypeId === selectedVehicleModelId // 过滤匹配车型的车辆
            )
            .map((vehicle: VehicleType) => ({
                label: `${vehicle.vehicleNumber}-${vehicle.licensePlate}`,
                value: vehicle.id
            }));
    }, [selectedVehicleModelId, vehiclesWithModelData]);

    // Handle vehicle model change
    const handleVehicleModelChange = (value: number) => {
        setSelectedVehicleModelId(value);
        // 清空车辆选择，因为需要根据新的车型重新选择
        form.setFieldsValue({
            vehicleId: undefined
        });
    };

    // Prepare pickup point options (for origin)
    const pickupPointOptions = (() => {
        const options: PickupPointOption[] = [];

        if (pickupPointsData?.getPickupPointOptions) {
            const {
                hubList,
                warehouseList,
                rallyPointList,
                firstAppointmentAddress
            } = pickupPointsData.getPickupPointOptions;

            // Add hub options
            hubList.forEach(hub => {
                options.push({
                    label: hub.name,
                    value: `1_${hub.value}`,
                    address: hub.fullAddress || '',
                    zipcode: hub.value || ''
                });
            });

            // Add rally point options
            rallyPointList.forEach(rallyPoint => {
                options.push({
                    label: rallyPoint.name,
                    value: `2_${rallyPoint.value}`,
                    address: rallyPoint.fullAddress || '',
                    zipcode: rallyPoint.value || ''
                });
            });

            // Add first appointment address if exists
            if (firstAppointmentAddress) {
                options.push({
                    label: firstAppointmentAddress.name,
                    value: `3_${firstAppointmentAddress.value}`,
                    address: firstAppointmentAddress.fullAddress || '',
                    zipcode: firstAppointmentAddress.value || '',
                });
            }

            warehouseList.forEach(warehouse => {
                options.push({
                    label: warehouse.name,
                    value: `4_${warehouse.value}`,
                    address: warehouse.fullAddress || '',
                    zipcode: warehouse.value || ''
                });
            });
        }

        return options;
    })();

    // Prepare destination options (only hubs)
    const destinationOptions = useMemo(() => {
        const options: PickupPointOption[] = [];

        if (hubListData?.hubList) {
            hubListData.hubList
                .filter(hub => hub.id === warehouseId)
                .forEach(hub => {
                    options.push({
                        label: hub.name || '',
                        value: `1_${hub.id}`,
                        address: hub.addressLine1 || '',
                        zipcode: hub.zipcode || '',
                        pickupDeadlineTime: hub.pickupDeadlineTime || undefined
                    });
                });
        }

        return options;
    }, [hubListData]);

    // 添加获取选中目的地hub的pickup deadline time限制的函数
    const getSelectedHubPickupDeadlineTime = () => {
        return selectedDestinationHub?.pickupDeadlineTime || null;
    };

    // Handle origin point change
    const handleOriginChange = (value: string) => {
        const selectedPoint = pickupPointOptions.find((p) => p.value === value);

        if (selectedPoint) {
            form.setFieldsValue({
                startPointAddress: selectedPoint.address,
                startPointZipcode: selectedPoint.zipcode
            });
        }
    };

    // Handle destination point change
    const handleDestinationChange = (value: string) => {
        const selectedPoint = destinationOptions.find((p) => p.value === value);

        if (selectedPoint) {
            form.setFieldsValue({
                endPointAddress: selectedPoint.address,
                endPointZipcode: selectedPoint.zipcode
            });

            // Find the corresponding hub with pickup deadline time
            const [, hubId] = value.split('_');
            const selectedHub = hubListData?.hubList.find(hub =>
                hub.id === hubId
            );
            console.log('Finding hub:', { hubId, foundHub: selectedHub?.name, allHubs: hubListData?.hubList?.map(h => ({ id: h.id, name: h.name })) });
            setSelectedDestinationHub(selectedHub || null);

            // 当destination hub改变时，重新验证Expected Departure Time字段
            // 因为pickup deadline time限制可能已经改变
            const expDepartureTime = form.getFieldValue('expDepartureTime');
            if (expDepartureTime) {
                form.validateFields(['expDepartureTime']).catch(() => {
                    // 验证失败时不需要特殊处理，错误信息会自动显示
                });
            }
        } else {
            setSelectedDestinationHub(null);
        }

        // Force table summary to re-render
        setSummaryUpdateKey(prev => prev + 1);
    };

    // Move appointment up in the list
    const handleMoveUp = (id: number) => {
        if (!appointmentList) return;

        const appointments = [...appointmentList];
        const index = appointments.findIndex(item => item.id === id);

        if (index > 0) {
            // Swap positions
            const temp = appointments[index];
            appointments[index] = appointments[index - 1];
            appointments[index - 1] = temp;

            // Update sequence
            setAppointmentList(appointments);
        }
    };

    // Move appointment down in the list
    const handleMoveDown = (id: number) => {
        if (!appointmentList) return;

        const appointments = [...appointmentList];
        const index = appointments.findIndex(item => item.id === id);

        if (index < appointments.length - 1) {
            // Swap positions
            const temp = appointments[index];
            appointments[index] = appointments[index + 1];
            appointments[index + 1] = temp;

            // Update sequence
            setAppointmentList(appointments);
        }
    };

    // Remove appointment from the list
    const handleRemove = (id: number) => {
        if (!appointmentList) return;

        // Create new appointment list without the removed item
        const newAppointmentList = appointmentList.filter(item => item.id !== id);
        setAppointmentList(newAppointmentList);
    };

    // Add new appointment
    const handleAddAppointment = () => {
        setAddModalVisible(true);
    };

    // Handle appointment add success
    const handleAddAppointmentSuccess = (selectedAppointments: PickupAppointmentType[]) => {
        message.success(`Successfully added ${selectedAppointments.length} appointment(s)`);

        // Add new appointments to the list
        const newAppointmentList = [...appointmentList];

        selectedAppointments.forEach(appointment => {
            // Check if appointment already exists in the list
            if (!newAppointmentList.some(item => item.id === appointment.id)) {
                newAppointmentList.push(appointment);
            }
        });

        setAppointmentList(newAppointmentList);
        setAddModalVisible(false);
    };

    // Calculate summary statistics
    const calculateSummary = () => {
        if (!appointmentList || appointmentList.length === 0) {
            return {
                appointmentCount: 0,
                quantity: '0',
                distance: 0,
                duration: 0
            };
        }

        const totalAppointments = appointmentList.length;

        // Count pallets and pieces separately
        let palletCount = 0;
        let pieceCount = 0;
        let boxCount = 0;
        let bagCount = 0;

        appointmentList.forEach(appointment => {
            const count = appointment.expectedParcelCount || 0;

            if (appointment.expectedParcelUnit === 'PIECE') {
                pieceCount += count;
            } else if (appointment.expectedParcelUnit === 'BAG') {
                bagCount += count;
            } else if (appointment.expectedParcelUnit === 'CARTON') {
                boxCount += count;
            } else {
                palletCount += count;
            }
        });

        // Format quantity display
        let quantityDisplay = '';

        if (pieceCount > 0) {
            const unitLabel = packageUnitFilterOptions.find(opt => opt.value === 'PIECE')?.label || 'Piece';
            quantityDisplay += `${pieceCount} ${unitLabel}`;
        }

        if (bagCount > 0) {
            if (quantityDisplay) quantityDisplay += '+';
            const unitLabel = packageUnitFilterOptions.find(opt => opt.value === 'BAG')?.label || 'Bag';
            quantityDisplay += `${bagCount} ${unitLabel}`;
        }

        if (boxCount > 0) {
            if (quantityDisplay) quantityDisplay += '+';
            const unitLabel = packageUnitFilterOptions.find(opt => opt.value === 'CARTON')?.label || 'Carton';
            quantityDisplay += `${boxCount} ${unitLabel}`;
        }

        if (palletCount > 0) {
            if (quantityDisplay) quantityDisplay += '+';
            const unitLabel = packageUnitFilterOptions.find(opt => opt.value === 'PALLET')?.label || 'Pallet';
            quantityDisplay += `${palletCount} ${unitLabel}`;
        }

        if (!quantityDisplay) quantityDisplay = '0';

        // Calculate estimated distance and duration (using actual values from the table)
        let totalDistance = 0;
        let totalDuration = 0;

        // Estimated distance and duration for each point
        appointmentList.forEach(() => {
            totalDistance += 0.0; // Use actual distance value
            totalDuration += 0.0; // Use actual duration value
        });

        return {
            appointmentCount: totalAppointments,
            quantity: quantityDisplay,
            distance: totalDistance.toFixed(1),
            duration: totalDuration.toFixed(1)
        };
    };

    const summary = calculateSummary();

    // 添加重置函数，供外部调用
    const resetFormAndState = () => {
        form.resetFields();
        // 使用传入的 pickupDate 或默认为今天
        const taskDate = pickupDate ? dayjs(pickupDate) : dayjs();
        form.setFieldsValue({
            taskDate: taskDate,
            status: 'DRAFT'
        });
        setAppointmentList([]);
        setAddModalVisible(false);
        setLoading(false);
        setSelectedVehicleModelId(null);
        setSelectedDestinationHub(null);
        setSelectedDriverId(null);
    };

    return {
        form,
        loading,
        appointmentList,
        addModalVisible,
        driverOptions,
        driversLoading,
        pickupPointOptions,
        pickupPointsLoading,
        destinationOptions, // 新增destination选项
        vehicleModelOptions,
        vehicleModelsLoading,
        vehicleOptions: filteredVehicleOptions, // 返回过滤后的车辆选项
        vehiclesLoading: vehiclesWithModelLoading, // 使用新查询的loading状态
        appointmentsLoading,
        summary,
        summaryUpdateKey,
        selectedVehicleModelId, // 暴露选中的车型ID
        selectedDestinationHub, // 暴露选中的hub信息
        getSelectedHubPickupDeadlineTime, // 暴露获取pickup deadline time的函数
        handleSave,
        handleDriverChange, // 新增司机变更处理函数
        handleVehicleModelChange, // 新增车型变更处理函数
        handleOriginChange,
        handleDestinationChange,
        handleMoveUp,
        handleMoveDown,
        handleRemove,
        handleAddAppointment,
        handleAddAppointmentSuccess,
        setAddModalVisible,
        resetFormAndState,
        pickupDate, // 暴露 pickupDate 供组件使用
        warehouseId // 暴露 warehouseId 供组件使用
    };
};
